import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import VoiceRecorder from './VoiceRecorder';
import { VoiceSettingsType } from '../types';

interface VoiceControlsProps {
  interviewId: string | null;
  onVoiceMessage: (text: string) => void;
  onVoiceResponse: (audioData: string, text: string) => void;
  onEmptyAudioError?: (message: string) => void;
  disabled?: boolean;
  isPlaying?: boolean;
  onPlaybackStart?: () => void;
  onPlaybackEnd?: () => void;
  onConnectionStatusChange?: (isConnected: boolean) => void;
  onProcessingStatusChange?: (isProcessing: boolean) => void;
  // WebSocket connection from parent
  webSocket?: WebSocket | null;
  // onAudioData prop removed to prevent duplicate audio handling
  voiceSettings: VoiceSettingsType;
  setVoiceSettings: React.Dispatch<React.SetStateAction<VoiceSettingsType>>;
  onLiveTranscriptionUpdate?: (text: string) => void;
}

interface VoiceControlsRef {
  handleMessage: (data: any) => void;
}

const VoiceControls = forwardRef<VoiceControlsRef, VoiceControlsProps>(({
  interviewId,
  onVoiceMessage,
  onVoiceResponse,
  onEmptyAudioError,
  disabled = false,
  isPlaying = false,
  onPlaybackStart,
  onPlaybackEnd,
  onConnectionStatusChange,
  onProcessingStatusChange,
  // WebSocket connection from parent
  webSocket,
  voiceSettings,
  setVoiceSettings,
  onLiveTranscriptionUpdate
}, ref) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [currentResponseAudio, setCurrentResponseAudio] = useState<string>('');
  const [currentResponseText, setCurrentResponseText] = useState<string>('');
  const [currentResponseFormat, setCurrentResponseFormat] = useState<string>('wav');
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcription, setTranscription] = useState<string>('');
  const [voiceEnabled] = useState(true);
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  const [testAudioData, setTestAudioData] = useState<string>('');
  const [testAudioFormat, setTestAudioFormat] = useState<string>('wav');
  const [isGeneratingTest, setIsGeneratingTest] = useState(false);
  const [sampleText, setSampleText] = useState("Hello! This is a test of the current voice settings. You can hear how the speed, pitch, and accent sound with this sample text.");
  const [isSystemMessage, setIsSystemMessage] = useState(false);
  
  // TTS Engine state
  const [availableEngines, setAvailableEngines] = useState<{[key: string]: any}>({});
  const [isLoadingEngines, setIsLoadingEngines] = useState(false);

  // WebSocket connection
  const wsRef = useRef<WebSocket | null>(null);
  
  // Simple audio element ref for reliable playback
  const audioRef = useRef<HTMLAudioElement>(null);
  
  // Progressive audio playback state
  const [isPlayingQueue, setIsPlayingQueue] = useState(false);
  
  // Add state tracking for chunk management
  const [receivedChunkCount, setReceivedChunkCount] = useState(0);
  const [isWaitingForMoreChunks, setIsWaitingForMoreChunks] = useState(false);
  
  // Ref to track current audio queue for immediate access
  const audioQueueRef = useRef<HTMLAudioElement[]>([]);
  // Ref to track current chunk index for immediate access
  const currentChunkIndexRef = useRef<number>(0);
  // Ref to track waiting state for immediate access in callbacks
  const isWaitingForMoreChunksRef = useRef<boolean>(false);
  
  // Load available TTS engines
  useEffect(() => {
    const loadEngines = async () => {
      try {
        setIsLoadingEngines(true);
        const response = await fetch('/api/voice/engines');
        if (response.ok) {
          const engines = await response.json();
          setAvailableEngines(engines);
          
          // Find the current engine
          for (const [, engineInfo] of Object.entries(engines)) {
            if ((engineInfo as any).current) {
              // setCurrentEngine(engineName); // This line was removed
              break;
            }
          }
        }
      } catch (error) {
        console.error('Error loading TTS engines:', error);
      } finally {
        setIsLoadingEngines(false);
      }
    };

    loadEngines();
  }, []);

  // Switch TTS engine
  const switchEngine = async (engineName: string) => {
    try {
      const response = await fetch('/api/voice/engines/switch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ engine_name: engineName })
      });
      
      if (response.ok) {
        // setCurrentEngine(engineName); // This line was removed
        console.log(`Switched to ${engineName} engine`);
        
        // Reload available engines to update current status
        const enginesResponse = await fetch('/api/voice/engines');
        if (enginesResponse.ok) {
          const engines = await enginesResponse.json();
          setAvailableEngines(engines);
        }
      } else {
        console.error('Failed to switch TTS engine');
      }
    } catch (error) {
      console.error('Error switching TTS engine:', error);
    }
  };

  // Use WebSocket from parent instead of creating our own
  useEffect(() => {
    if (webSocket) {
      wsRef.current = webSocket;
      
      // Update connection status based on WebSocket state
      const updateConnectionStatus = () => {
        const isConnected = webSocket.readyState === WebSocket.OPEN;
        setIsConnected(isConnected);
        onConnectionStatusChange?.(isConnected);
        if (!isConnected) {
          setConnectionError('WebSocket disconnected');
        } else {
          setConnectionError(null);
        }
      };
      
      updateConnectionStatus();
      
      // Set up event listeners
      webSocket.addEventListener('open', () => {
        console.log('✅ VoiceControls: WebSocket connected');
        updateConnectionStatus();
      });
      
      webSocket.addEventListener('close', (event) => {
        console.log('❌ VoiceControls: WebSocket disconnected', event.code, event.reason);
        updateConnectionStatus();
        
        // Provide more specific error messages based on close code
        if (event.code === 1006) {
          setConnectionError('Connection lost unexpectedly. Please refresh the page.');
        } else if (event.code === 1015) {
          setConnectionError('Server error. Please try again later.');
        } else if (event.code !== 1000) {
          setConnectionError(`Connection closed (${event.code}). Please refresh the page.`);
        }
      });
      
      webSocket.addEventListener('error', (error) => {
        console.error('❌ VoiceControls: WebSocket error', error);
        setConnectionError('Connection error. Please check your internet connection.');
        setIsConnected(false);
        onConnectionStatusChange?.(false);
      });
      
      return () => {
        webSocket.removeEventListener('open', updateConnectionStatus);
        webSocket.removeEventListener('close', updateConnectionStatus);
        webSocket.removeEventListener('error', updateConnectionStatus);
      };
    }
  }, [webSocket, onConnectionStatusChange]);

  // Progressive audio playback functions
  const playNextChunk = useCallback(() => {
    if (currentChunkIndexRef.current < audioQueueRef.current.length) {
      const audioElement = audioQueueRef.current[currentChunkIndexRef.current];
      console.log(`[AUDIO PLAYBACK] Attempting to play chunk ${currentChunkIndexRef.current + 1}/${audioQueueRef.current.length}`);
      
      audioElement.onplay = () => {
        console.log(`[AUDIO PLAYBACK] Playback started for chunk ${currentChunkIndexRef.current + 1}`);
      };
      audioElement.onended = () => {
        console.log(`[AUDIO PLAYBACK] Playback ended for chunk ${currentChunkIndexRef.current + 1}`);
        currentChunkIndexRef.current = currentChunkIndexRef.current + 1;
        
        // Add a short pause between chunks for more natural playback
        if (currentChunkIndexRef.current < audioQueueRef.current.length) {
          console.log(`[AUDIO PLAYBACK] Adding 200ms pause before next chunk`);
          setTimeout(() => {
            playNextChunk();
          }, 400);
        } else {
          // Check if we're expecting more chunks
          if (isWaitingForMoreChunksRef.current) {
            console.log('[AUDIO PLAYBACK] Waiting for more chunks to arrive...');
            // Don't end playback yet, just wait
            return;
          }
          // No more chunks, proceed immediately
          playNextChunk();
        }
      };
      audioElement.onerror = (error) => {
        console.error(`[AUDIO PLAYBACK] Error playing chunk ${currentChunkIndexRef.current + 1}:`, error);
        currentChunkIndexRef.current = currentChunkIndexRef.current + 1;
        
        // Add a short pause between chunks even on error
        if (currentChunkIndexRef.current < audioQueueRef.current.length) {
          console.log(`[AUDIO PLAYBACK] Adding 200ms pause before next chunk (after error)`);
          setTimeout(() => {
            playNextChunk();
          }, 400);
        } else {
          // Check if we're expecting more chunks
          if (isWaitingForMoreChunksRef.current) {
            console.log('[AUDIO PLAYBACK] Waiting for more chunks to arrive (after error)...');
            return;
          }
          // No more chunks, proceed immediately
          playNextChunk();
        }
      };
      
      audioElement.play().catch(err => {
        console.error(`[AUDIO PLAYBACK] play() failed for chunk ${currentChunkIndexRef.current + 1}:`, err);
        currentChunkIndexRef.current = currentChunkIndexRef.current + 1;
        
        // Add a short pause between chunks even on play failure
        if (currentChunkIndexRef.current < audioQueueRef.current.length) {
          console.log(`[AUDIO PLAYBACK] Adding 200ms pause before next chunk (after play failure)`);
          setTimeout(() => {
            playNextChunk();
          }, 400);
        } else {
          // Check if we're expecting more chunks
          if (isWaitingForMoreChunksRef.current) {
            console.log('[AUDIO PLAYBACK] Waiting for more chunks to arrive (after play failure)...');
            return;
          }
          // No more chunks, proceed immediately
          playNextChunk();
        }
      });
    } else {
      // Check if we're expecting more chunks
      if (isWaitingForMoreChunksRef.current) {
        console.log('[AUDIO PLAYBACK] Waiting for more chunks to arrive...');
        // Don't end playback yet, just wait
        return;
      }
      
      // All chunks played and no more expected
      console.log('[AUDIO PLAYBACK] All audio chunks finished playing');
      setIsPlayingQueue(false);
      currentChunkIndexRef.current = 0;
      audioQueueRef.current = [];
      onPlaybackEnd?.();
      setIsProcessing(false);
      onProcessingStatusChange?.(false);
      setIsSystemMessage(false);
      
      // Reset chunk tracking for next message
      setReceivedChunkCount(0);
      setIsWaitingForMoreChunks(false);
    }
  }, [audioQueueRef, onPlaybackEnd, onProcessingStatusChange]);

  const startProgressivePlayback = useCallback(() => {
    if (audioQueueRef.current.length > 0 && !isPlayingQueue) {
      console.log(`🎵 Starting progressive playback with ${audioQueueRef.current.length} chunks`);
      setIsPlayingQueue(true);
      currentChunkIndexRef.current = 0;
      onPlaybackStart?.();
      playNextChunk();
    }
  }, [audioQueueRef, isPlayingQueue, onPlaybackStart, playNextChunk]);



  // Cleanup when interviewId changes or component unmounts
  useEffect(() => {
    return () => {
      // Clear audio state to prevent stale data
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      
      // Reset chunk tracking for new message
      setReceivedChunkCount(0);
      setIsWaitingForMoreChunks(false);
      isWaitingForMoreChunksRef.current = false;
      audioQueueRef.current = [];
      currentChunkIndexRef.current = 0;
      setIsPlayingQueue(false);
    };
  }, [interviewId]);

  // Handle audio recording - REMOVED to prevent duplicate sending
  // Audio handling is now exclusively done by ChatInput/VoiceRecorder
  // This prevents duplicate WebSocket messages being sent to the backend
  const handleAudioData = async (audioBlob: Blob) => {
    console.log('🎤 VoiceControls: Audio handling removed to prevent duplicates');
    console.log('🎤 VoiceControls: Audio should be handled by ChatInput/VoiceRecorder only');
    // Do nothing - let ChatInput handle all audio sending
  };

  // Handle messages from App.tsx (centralized WebSocket handling)
  const handleMessage = useCallback((data: any) => {
    console.log('🎵 VoiceControls: Received message from App.tsx:', data.type);
    
          // Reset chunk tracking when a new message starts
      if (data.type === 'response_audio_chunk' && data.chunk_index === 1) {
        console.log('🎵 New message starting, resetting chunk tracking');
        setReceivedChunkCount(0);
        setIsWaitingForMoreChunks(false);
        isWaitingForMoreChunksRef.current = false;
        audioQueueRef.current = [];
        currentChunkIndexRef.current = 0;
        setIsPlayingQueue(false);
      }
    
    if (data.type === 'response_audio_chunk') {
      console.log('🎵 VoiceControls handling response_audio_chunk:', {
        type: data.type,
        format: data.format,
        audioLength: data.audio.length,
        engine: data.engine,
        is_final: data.is_final,
        chunk_index: data.chunk_index
      });
      
      // Handle streaming audio chunk with progressive playback
      if (data.audio && data.audio.length > 0) {
        // Create audio element for this chunk
        const dataUrl = `data:audio/${data.format || 'wav'};base64,${data.audio}`;
        const audioElement = new Audio(dataUrl);
        
        // Add to queue (ref only)
        audioQueueRef.current = [...audioQueueRef.current, audioElement];
        
        // Update chunk count and determine if we should start playback
        const newChunkCount = receivedChunkCount + 1;
        setReceivedChunkCount(newChunkCount);
        
        // Determine if we should start playback
        const shouldStartPlayback = 
          newChunkCount >= 2 || // Wait for 2 chunks
          (newChunkCount === 1 && data.is_final); // Or start immediately if single chunk is final
        
        if (shouldStartPlayback && !isPlayingQueue) {
          console.log(`🎵 Starting playback with ${newChunkCount} chunks (${data.is_final ? 'final' : 'more expected'})`);
          setTimeout(() => startProgressivePlayback(), 50);
        } else if (newChunkCount === 1 && !data.is_final) {
          console.log('🎵 Waiting for more chunks before starting playback...');
          setIsWaitingForMoreChunks(true);
          isWaitingForMoreChunksRef.current = true;
        }
        
        // If this is the final chunk, update processing state and stop waiting
        if (data.is_final) {
          setIsWaitingForMoreChunks(false);
          isWaitingForMoreChunksRef.current = false;
          setIsProcessing(false);
          onProcessingStatusChange?.(false);
          console.log('🎤 Streaming TTS completed - thinking indicator hidden');
          
          // If we haven't started playback yet and this is the final chunk, start now
          if (!isPlayingQueue && newChunkCount > 0) {
            console.log('🎵 Final chunk received, starting playback immediately');
            setTimeout(() => startProgressivePlayback(), 50);
          }
          
          // Note: System message detection moved to response_text handler
          console.log('🎵 Audio chunk streaming completed');
        }
      }
    } else if (data.type === 'response_audio' && data.audio) {
      console.log('🎵 VoiceControls handling response_audio:', {
        type: data.type,
        format: data.format,
        audioLength: data.audio.length,
        engine: data.engine
      });
      
      // Check for empty audio data
      if (data.audio.length === 0) {
        console.error('🎵 Empty audio data received - skipping audio playback');
        return;
      }
      
      setCurrentResponseAudio(data.audio);
      setCurrentResponseFormat(data.format || 'wav');
      
      // Reset processing state since we now have the audio response
      setIsProcessing(false);
      onProcessingStatusChange?.(false);
      console.log('🎤 Voice processing completed - thinking indicator hidden');
      
      // Only handle audio playback, no text content
      onPlaybackStart?.();
      
      // Start audio playback after a short delay
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play().catch(err => {
            console.error('Failed to play audio:', err);
          });
        }
      }, 100);
    } else if (data.type === 'response_text' && data.text) {
      console.log('🎵 VoiceControls handling response_text:', data.text);
      
      setCurrentResponseText(data.text);
      
      // Detect if this is a system message
      const isSystem = detectSystemMessage(data.text);
      setIsSystemMessage(isSystem);
      console.log('🎵 Text response type detected:', isSystem ? 'System message' : 'Regular AI response');
      
      // Reset processing state since we now have the text response
      setIsProcessing(false);
      onProcessingStatusChange?.(false);
      console.log('🎤 Voice processing completed - thinking indicator hidden');
      
      // Send text response to parent for immediate display and chat history
      onVoiceResponse('', data.text);
    } else if (data.type === 'transcription' && data.text) {
      console.log('VoiceControls handling transcription:', data.text);
      setTranscription(data.text);
      onVoiceMessage(data.text);
    } else if (data.type === 'transcription_partial' && data.text) {
      console.log('VoiceControls handling partial transcription:', data.text);
      setTranscription(data.text);
    } else if (data.type === 'transcription_error') {
      console.error('VoiceControls handling transcription error:', data.error);
      setConnectionError(`Transcription error: ${data.error}`);
      setIsProcessing(false);
      onProcessingStatusChange?.(false);
    } else if (data.type === 'empty_audio_error') {
      console.log('VoiceControls handling empty audio error:', data.message);
      setIsProcessing(false);
      onProcessingStatusChange?.(false);
      console.log('🎤 Voice processing stopped due to empty audio');
      
      // Call the parent handler to add the error message to chat history
      onEmptyAudioError?.(data.message);
    } else if (data.type === 'error') {
      console.error('VoiceControls handling error:', data.message);
      setConnectionError(data.message);
      setIsProcessing(false);
      onProcessingStatusChange?.(false);
      console.log('🎤 Voice processing stopped due to error');
    }
  }, [onProcessingStatusChange, receivedChunkCount, isPlayingQueue, startProgressivePlayback]);

  // Expose the handler via ref
  useImperativeHandle(ref, () => ({
    handleMessage
  }));

  // Test voice with current settings
  const testVoice = async () => {
    try {
      setIsGeneratingTest(true);
      setTestAudioData('');
      
      const response = await fetch('/api/voice/test-sample', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sample_text: sampleText, voice_settings: voiceSettings })
      });
      
      if (response.ok) {
        const data = await response.json();
        setTestAudioData(data.sample_audio);
        setTestAudioFormat(data.format);
        console.log('Test audio generated with settings:', data.settings_used);
      } else {
        console.error('Failed to generate test audio');
      }
    } catch (error) {
      console.error('Error testing voice:', error);
    } finally {
      setIsGeneratingTest(false);
    }
  };

  // Detect system messages (like initial greetings)
  const detectSystemMessage = (text: string): boolean => {
    const systemKeywords = [
      'hello', 'hi', 'welcome', 'greetings', 'good', 'morning', 'afternoon', 'evening',
      'thank you for joining', 'nice to meet you', 'pleasure to meet you',
      'let\'s get started', 'shall we begin', 'ready to start'
    ];
    
    const lowerText = text.toLowerCase();
    return systemKeywords.some(keyword => lowerText.includes(keyword));
  };

  return (
    <div className="space-y-4">
      {/* Voice Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 mt-2">
          
          
          {/* Voice Settings Button */}
          {voiceEnabled && (
            <button
              onClick={() => setShowVoiceSettings(!showVoiceSettings)}
              className="px-4 py-2 mt-2 text-xs border-gray-200 border hover:bg-gray-200 text-gray-700 rounded-md hidden"
            >
              Settings
            </button>
          )}
        </div>
        
        {isProcessing && (
          <div className="flex items-center space-x-2 text-primary-600">
            <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Processing...</span>
          </div>
        )}
      </div>

      {/* Voice Settings Panel */}
      {voiceEnabled && showVoiceSettings && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
          <div className="text-sm font-medium text-gray-700">Voice Settings</div>
          
          {/* TTS Engine Switcher */}
          <div className="space-y-2">
            
            {isLoadingEngines ? (
              <div className="flex items-center space-x-2 text-gray-500">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                <span className="text-xs">Loading engines...</span>
              </div>
            ) : (
              <div className="flex flex-row  gap-4" >
                {Object.entries(availableEngines).map(([engineName, engineInfo]) => {
                  const info = engineInfo as any;
                  return (
                    <div key={engineName} className="flex flex-1 flex-col p-4 border border-gray-200 rounded-md">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium">{info.name}</span>
                          {info.current && (
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                              Current
                            </span>
                          )}
                        </div>
                        {info.note && (
                          <div className="text-xs text-gray-500 mt-1">{info.note}</div>
                        )}
                      </div>
                      <button
                        onClick={() => switchEngine(engineName)}
                        disabled={!info.available || info.current}
                        className={`px-3 py-1 mt-4 text-xs rounded-md ${
                          info.current
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : info.available
                            ? 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {info.current ? 'Active' : info.available ? 'Switch' : 'Unavailable'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          

          
          {/* Speech Speed */}
          <div className="space-y-1">
            <label className="text-xs text-gray-600">
              Speech Speed: {voiceSettings.speed}x
            </label>
            <input
              type="range"
              min="0.5"
              max="2.0"
              step="0.1"
              value={voiceSettings.speed}
              onChange={(e) => setVoiceSettings((prev: VoiceSettingsType) => ({ ...prev, speed: parseFloat(e.target.value) }))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>Slow (0.5x)</span>
              <span>Fast (2.0x)</span>
            </div>
          </div>
          
          {/* Volume */}
          <div className="space-y-1">
            <label className="text-xs text-gray-600">
              Volume: {Math.round(voiceSettings.volume * 100)}%
            </label>
            <input
              type="range"
              min="0.0"
              max="1.0"
              step="0.1"
              value={voiceSettings.volume}
              onChange={(e) => setVoiceSettings((prev: VoiceSettingsType) => ({ ...prev, volume: parseFloat(e.target.value) }))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>Quiet</span>
              <span>Loud</span>
            </div>
          </div>
          
          {/* Apply Settings Button */}
          <button
            onClick={async () => {
              try {
                const response = await fetch('/api/voice/configure', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(voiceSettings)
                });
                if (response.ok) {
                  console.log('Voice settings updated');
                }
              } catch (error) {
                console.error('Failed to update voice settings:', error);
              }
            }}
            className="px-3 py-2 text-sm bg-primary-500 hover:bg-primary-600 text-white rounded-md"
          >
            Apply Settings
          </button>
          
          {/* Test Voice Section */}
          <div className="border-t pt-3 space-y-3">
            <div className="text-sm font-medium text-gray-700">Test Voice</div>
            
            {/* Sample Text Input */}
            <div className="space-y-1">
              <label className="text-xs text-gray-600">Sample Text</label>
              <textarea
                value={sampleText}
                onChange={(e) => setSampleText(e.target.value)}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md resize-none"
                rows={3}
                placeholder="Enter text to test voice settings..."
              />
            </div>
            
            {/* Test Button */}
            <button
              onClick={testVoice}
              disabled={isGeneratingTest}
              className="px-3 py-2 text-sm bg-primary-500 hover:bg-primary-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed space-x-2"
            >
              {isGeneratingTest ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <span>Test Voice</span>
                </>
              )}
            </button>
            
            {/* Test Audio Player */}
            {testAudioData && (
              <div className="space-y-2">
                <div className="text-xs text-gray-600">Test Audio:</div>
                <audio
                  src={`data:${testAudioFormat};base64,${testAudioData}`}
                  controls
                  autoPlay
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Voice Recorder */}
      {/* Temporarily hidden - keeping implementation intact */}
      {false && voiceEnabled && (
        <div className="space-y-4">
          <VoiceRecorder
            onAudioData={handleAudioData}
            onTranscription={setTranscription}
            isRecording={isRecording}
            onRecordingStateChange={setIsRecording}
            disabled={disabled || !isConnected || isProcessing}
          />
          
          {/* Live Transcription */}
          {transcription && (
            <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg">
              <div className="text-sm text-primary-800 font-medium mb-1">
                You said:
              </div>
              <div className="text-primary-900">{transcription}</div>
            </div>
          )}
        </div>
      )}

      {/* Progressive Audio Playback Progress */}
      {isPlayingQueue && audioQueueRef.current.length > 0 && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg hidden">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-blue-800">
              Playing Audio Response
            </div>
            <div className="text-xs text-blue-600">
              {currentChunkIndexRef.current + 1} / {audioQueueRef.current.length} chunks
            </div>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentChunkIndexRef.current + 1) / audioQueueRef.current.length) * 100}%` }}
            />
          </div>
          <div className="text-xs text-blue-600 mt-1">
            {/* Text content is handled separately via response_text messages */}
          </div>
        </div>
      )}

      {/* Debug Information (only show in development) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="p-2 bg-gray-100 border border-gray-200 rounded text-xs hidden">
          <div className="font-medium mb-1">Debug Info:</div>
          <div>Audio Queue Length: {audioQueueRef.current.length}</div>
          <div>Current Chunk Index: {currentChunkIndexRef.current}</div>
          <div>Is Playing Queue: {isPlayingQueue ? 'Yes' : 'No'}</div>
          <div>Full Response Text Length: {currentResponseText.length}</div>
          <div>Is System Message: {isSystemMessage ? 'Yes' : 'No'}</div>
        </div>
      )}

      {/* Simple Audio Player for AI Response */}
      {currentResponseAudio && currentResponseAudio.length > 0 && (
        <div className="space-y-2">
          <audio className='hidden'
            ref={audioRef}
            src={`data:audio/${currentResponseFormat};base64,${currentResponseAudio}`}
            onPlay={() => {
              console.log('🎵 Audio playback started');
              onPlaybackStart?.();
            }}
            onEnded={() => {
              console.log('🎵 Audio playback ended');
              onPlaybackEnd?.();
              setIsProcessing(false);
              onProcessingStatusChange?.(false);
              setIsSystemMessage(false);
            }}
            onError={(e) => {
              console.error('🎵 Audio playback error:', e);
              setIsProcessing(false);
              onProcessingStatusChange?.(false);
            }}
            controls={!isSystemMessage}
            autoPlay={true}
            style={{ display: isSystemMessage ? 'none' : 'block' }}
          />
        </div>
      )}


      {/* Error Display */}
      {connectionError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-sm text-red-800">
            <strong>Voice Error:</strong> {connectionError}
          </div>
          <button
            onClick={() => setConnectionError(null)}
            className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
          >
            Dismiss
          </button>
        </div>
      )}

    </div>
  );
});

export default VoiceControls; 